// Wizard step 5 — review & activate (slice 4a-3).
//
// Shows a settings summary with per-section "Edit" jumps, the activation
// warning copy, and the Activate button. The button is disabled until the
// draft passes the same gap check the server runs (collectProgramActivationGaps
// + the flat schema) — server stays authoritative, but the client mirrors it
// so the owner isn't surprised by a 422.

import { collectProgramActivationGaps, milestoneRuleIssues } from '@naya/shared'

import { grandPrize } from '../builderState.js'
import type { BuilderController } from '../hooks/useBuilder.js'
import type { WizardStepKey } from '../builderState.js'

function SummaryBlock({
  title,
  step,
  onEdit,
  children,
}: {
  title: string
  step: WizardStepKey
  onEdit: (step: WizardStepKey) => void
  children: React.ReactNode
}) {
  return (
    <div className="program-summary-block">
      <div className="program-summary-head">
        <h3>{title}</h3>
        <button
          type="button"
          className="program-link-btn"
          onClick={() => onEdit(step)}
        >
          Edit
        </button>
      </div>
      <div className="program-summary-body">{children}</div>
    </div>
  )
}

export function StepReview({
  builder,
  onEditStep,
  onRequestActivate,
}: {
  builder: BuilderController
  onEditStep: (step: WizardStepKey) => void
  /** Opens the confirmation modal — the actual API call lives in the wizard. */
  onRequestActivate: () => void
}) {
  const { draft } = builder
  const grand = grandPrize(draft.milestones)
  const intermediates = draft.milestones.filter((m) => m.type !== 'final')

  // Mirror the server's activation completeness check.
  const gaps = [
    ...milestoneRuleIssues(draft.milestones, draft.total_stamps),
    ...collectProgramActivationGaps({
      name: draft.name,
      total_stamps: draft.total_stamps,
      completion_behavior: draft.completion_behavior,
      milestones: draft.milestones,
      onboarding_fields: draft.onboarding_fields,
      pass_design: draft.pass_design,
      back_links: draft.back_links,
      owner_terms: draft.owner_terms,
      expiration_mode: draft.expiration_mode,
      expiration_relative_unit: draft.expiration_relative_unit,
      expiration_relative_amount: draft.expiration_relative_amount,
      expiration_fixed_date: draft.expiration_fixed_date,
    }),
  ]
  // De-dupe — the two checks overlap on milestone rules.
  const uniqueGaps = [...new Set(gaps)]
  const canActivate = uniqueGaps.length === 0

  const enabledOptional = (
    ['last_name', 'birthday', 'phone'] as const
  ).filter((f) => draft.onboarding_fields[f].enabled)

  return (
    <div className="program-step">
      <h2>Review and activate</h2>
      <p className="program-step-sub">
        Check everything over. Once you activate, customers can start enrolling.
      </p>

      <SummaryBlock title="Sign-up info" step="onboarding" onEdit={onEditStep}>
        <p>
          First name, Email
          {enabledOptional.length > 0 && (
            <>
              {', '}
              {enabledOptional
                .map((f) => f.replace('_', ' '))
                .join(', ')}
            </>
          )}
        </p>
      </SummaryBlock>

      <SummaryBlock title="Program" step="program" onEdit={onEditStep}>
        <p>
          {draft.total_stamps} stamps ·{' '}
          {draft.completion_behavior === 'reset'
            ? 'Resets for another round'
            : 'Marks complete forever'}
        </p>
        <ul className="program-summary-list">
          {intermediates.map((m, i) => (
            <li key={i}>
              Stamp {m.at_stamp}: {m.reward_text || <em>(no reward text)</em>}
            </li>
          ))}
          <li>
            <strong>
              Grand prize (stamp {draft.total_stamps}):{' '}
              {grand?.reward_text || <em>(no reward text)</em>}
            </strong>
          </li>
        </ul>
      </SummaryBlock>

      <SummaryBlock title="Pass front" step="front" onEdit={onEditStep}>
        <p className="program-summary-swatches">
          <span
            className="program-summary-swatch"
            style={{ background: draft.pass_design.primary_color }}
          />
          {draft.pass_design.primary_color}
          <span
            className="program-summary-swatch"
            style={{ background: draft.pass_design.text_color }}
          />
          {draft.pass_design.text_color}
        </p>
      </SummaryBlock>

      <SummaryBlock title="Pass back" step="back" onEdit={onEditStep}>
        <p>
          {draft.back_links.length} link
          {draft.back_links.length === 1 ? '' : 's'}
          {draft.owner_terms.trim() ? ' · custom terms added' : ''}
        </p>
      </SummaryBlock>

      {!canActivate && (
        <div className="program-gap-list" role="alert">
          <strong>Before you can activate:</strong>
          <ul>
            {uniqueGaps.map((g) => (
              <li key={g}>{g}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="program-activate-warning">
        After activating, you can't change the number of stamps, the
        milestones, or the completion behavior. You can still edit colors,
        links, and your terms later. Activate when you're ready to start
        enrolling customers.
      </div>

      <button
        type="button"
        className="owner-btn owner-btn-primary program-activate-btn"
        disabled={!canActivate}
        onClick={onRequestActivate}
      >
        Activate program
      </button>
    </div>
  )
}
